/**
 * AssetRadarGraph — a flat, at-a-glance overview of everything connected to a
 * single asset. The asset summary sits on top; below it is a simple grid of
 * category cards (3D Tags, Instrumentation, Documents, Maintenance, Risk,
 * History, Functional Location). Clicking a card expands an inline list of
 * the items in that category. No floating nodes, no animated spokes — just
 * cards and lists that read cleanly in both light and dark themes.
 */
import { useMemo, useState } from "react";
import {
  Box,
  MapPin,
  Tag,
  Radio,
  FolderTree,
  Wrench,
  ShieldAlert,
  History,
  ExternalLink,
  Cpu,
  FileText,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import type { Device as InstrumentationDevice, Sensor as InstrumentationSensor } from "../../lib/instrumentationApi";
import type { DMSDocument } from "../../lib/dmsApi";
import { formatDate, formatDateTime, truncate } from "../../lib/utils";
import { StatusBadge } from "../ui/StatusBadge";
import { Badge } from "../ui/Badge";

// ── Types pulled from AssetDetailPage (kept loose to avoid a circular import) ─

interface FunctionalLocationSummary {
  id: string;
  code: string;
  name: string;
  level: number;
  description?: string;
}

interface AssetForRadar {
  id: string;
  name: string;
  asset_code: string;
  status: string;
  description?: string;
  asset_type?: string;
  system?: string;
  sub_system?: string;
  functional_location?: FunctionalLocationSummary | null;
}

interface TagItem {
  id: string;
  label: string | null;
  tag_type: string;
  geometry: Record<string, unknown>;
  color: string | null;
  point_cloud_id: string | null;
  digital_twin_id: string | null;
  created_at: string;
}

interface MaintenanceTask {
  id: string;
  title: string;
  status: string;
  priority?: string;
  due_date?: string;
  task_type?: string;
  created_at: string;
}

interface RiskAssessment {
  id: string;
  risk_score?: number;
  likelihood?: number;
  consequence?: number;
  status: string;
  assessed_at: string;
}

interface HistoryEntry {
  id: string;
  action: string;
  changed_by_email?: string;
  changed_by_name?: string;
  changed_at: string;
  description?: string;
}

export interface AssetRadarGraphProps {
  asset: AssetForRadar;
  tags: TagItem[];
  maintenance: MaintenanceTask[];
  riskAssessments: RiskAssessment[];
  history: HistoryEntry[];
  devices: InstrumentationDevice[];
  sensors: InstrumentationSensor[];
  documents: DMSDocument[];
  onNavigate?: (path: string) => void;
}

// ── Categories ───────────────────────────────────────────────────────

type CategoryKey =
  | "floc"
  | "tags"
  | "instrumentation"
  | "documents"
  | "maintenance"
  | "risk"
  | "history";

interface CategoryMeta {
  key: CategoryKey;
  label: string;
  icon: LucideIcon;
  accent: string; // hex used for the icon tint only
}

const CATEGORIES: CategoryMeta[] = [
  { key: "floc",            label: "Functional Location", icon: MapPin,      accent: "#10b981" },
  { key: "tags",            label: "3D Tags",             icon: Tag,         accent: "#a855f7" },
  { key: "instrumentation", label: "Instrumentation",     icon: Radio,       accent: "#22d3ee" },
  { key: "documents",       label: "Documents",           icon: FolderTree,  accent: "#38bdf8" },
  { key: "maintenance",     label: "Maintenance",         icon: Wrench,      accent: "#f59e0b" },
  { key: "risk",            label: "Risk",                icon: ShieldAlert, accent: "#f43f5e" },
  { key: "history",         label: "History",             icon: History,     accent: "#94a3b8" },
];

const MAX_CHILDREN = 8;

interface ChildNodeData {
  id: string;
  label: string;
  sublabel?: string;
  category: CategoryKey;
  raw: unknown;
  navigateTo?: string;
}

// ── Component ────────────────────────────────────────────────────────

export function AssetRadarGraph(props: AssetRadarGraphProps) {
  const { asset, tags, maintenance, riskAssessments, history, devices, sensors, documents, onNavigate } = props;

  const [expanded, setExpanded] = useState<CategoryKey | null>(null);

  // Build the child list for each category. Cap at MAX_CHILDREN; the empty
  // state and the "+N more" hint are handled in the panel.
  const childrenByCategory = useMemo<Record<CategoryKey, ChildNodeData[]>>(() => {
    return {
      floc: asset.functional_location
        ? [{
            id: asset.functional_location.id,
            label: asset.functional_location.name,
            sublabel: `L${asset.functional_location.level} · ${asset.functional_location.code}`,
            category: "floc",
            raw: asset.functional_location,
          }]
        : [],
      tags: tags.slice(0, MAX_CHILDREN).map((t) => ({
        id: t.id,
        label: t.label || "Untagged",
        sublabel: t.tag_type,
        category: "tags",
        raw: t,
        navigateTo: t.digital_twin_id
          ? `/digital-twins/${t.digital_twin_id}?tag=${t.id}`
          : t.point_cloud_id
            ? `/point-clouds/${t.point_cloud_id}?tag=${t.id}`
            : undefined,
      })),
      instrumentation: [
        ...devices.slice(0, Math.ceil(MAX_CHILDREN / 2)).map((d) => ({
          id: `dev-${d.id}`,
          label: d.name,
          sublabel: `Device · ${d.source_type.replace(/_/g, " ")}`,
          category: "instrumentation" as const,
          raw: d,
          navigateTo: `/instrumentation/devices/${d.id}`,
        })),
        ...sensors.slice(0, Math.floor(MAX_CHILDREN / 2)).map((s) => ({
          id: `sen-${s.id}`,
          label: s.name,
          sublabel: `Sensor${s.sensor_type ? ` · ${s.sensor_type}` : ""}`,
          category: "instrumentation" as const,
          raw: s,
          navigateTo: `/instrumentation/sensors/${s.id}`,
        })),
      ].slice(0, MAX_CHILDREN),
      documents: documents.slice(0, MAX_CHILDREN).map((d) => ({
        id: d.id,
        label: d.name,
        sublabel: d.document_type.toUpperCase(),
        category: "documents",
        raw: d,
        navigateTo: `/dms?document=${d.id}`,
      })),
      maintenance: maintenance.slice(0, MAX_CHILDREN).map((m) => ({
        id: m.id,
        label: m.title,
        sublabel: m.task_type ? `${m.task_type} · ${m.status}` : m.status,
        category: "maintenance",
        raw: m,
        navigateTo: `/maintenance?task=${m.id}`,
      })),
      risk: riskAssessments.slice(0, MAX_CHILDREN).map((r) => ({
        id: r.id,
        label: `Score ${r.risk_score ?? "—"}`,
        sublabel: `L${r.likelihood ?? "—"} × C${r.consequence ?? "—"}`,
        category: "risk",
        raw: r,
      })),
      history: history.slice(0, MAX_CHILDREN).map((h) => ({
        id: h.id,
        label: h.action,
        sublabel: h.changed_by_name || h.changed_by_email || "Unknown",
        category: "history",
        raw: h,
      })),
    };
  }, [asset, tags, maintenance, riskAssessments, history, devices, sensors, documents]);

  // Total counts (un-capped) — shown on each category card.
  const counts: Record<CategoryKey, number> = {
    floc: asset.functional_location ? 1 : 0,
    tags: tags.length,
    instrumentation: devices.length + sensors.length,
    documents: documents.length,
    maintenance: maintenance.length,
    risk: riskAssessments.length,
    history: history.length,
  };

  return (
    <div className="liquid-glass rounded-2xl p-5 space-y-5">
      {/* Asset summary */}
      <AssetSummary asset={asset} />

      {/* Category grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {CATEGORIES.map((c) => {
          const count = counts[c.key];
          const isExpanded = expanded === c.key;
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => setExpanded((cur) => (cur === c.key ? null : c.key))}
              className={[
                "flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors",
                isExpanded
                  ? "border-surface-500/60 bg-surface-700/50"
                  : "border-surface-700/60 bg-surface-800/50 hover:bg-surface-700/40",
              ].join(" ")}
              title={`${c.label} — ${count} ${count === 1 ? "item" : "items"}`}
            >
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{ background: `${c.accent}22` }}
              >
                <Icon className="h-4 w-4" style={{ color: c.accent }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-surface-400">
                  {c.label}
                </div>
                <div className="text-sm font-semibold text-surface-100">
                  {count} {count === 1 ? "item" : "items"}
                </div>
              </div>
              <ChevronRight
                className={[
                  "h-4 w-4 shrink-0 text-surface-400 transition-transform",
                  isExpanded ? "rotate-90" : "",
                ].join(" ")}
              />
            </button>
          );
        })}
      </div>

      {/* Inline expansion panel */}
      {expanded && (
        <CategoryPanel
          category={CATEGORIES.find((c) => c.key === expanded)!}
          total={counts[expanded]}
          children={childrenByCategory[expanded]}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

// ── Asset summary header ─────────────────────────────────────────────

function AssetSummary({ asset }: { asset: AssetForRadar }) {
  return (
    <div className="flex flex-wrap items-start gap-4 rounded-xl border border-surface-700/60 bg-surface-800/40 p-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-500/15">
        <Box className="h-6 w-6 text-brand-400" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-surface-400">Asset</div>
        <div className="text-base font-semibold text-surface-100">{asset.name}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-surface-300">
          <span className="font-mono">{asset.asset_code}</span>
          <StatusBadge status={asset.status} />
          {asset.asset_type && (
            <span className="capitalize">{asset.asset_type.replace(/_/g, " ")}</span>
          )}
          {asset.system && <span className="capitalize">{asset.system}</span>}
          {asset.sub_system && <span>{asset.sub_system}</span>}
        </div>
        {asset.description && (
          <p className="mt-2 text-xs text-surface-300">{asset.description}</p>
        )}
      </div>
    </div>
  );
}

// ── Inline panel showing the items in the expanded category ─────────

function CategoryPanel({
  category, total, children, onNavigate,
}: {
  category: CategoryMeta;
  total: number;
  children: ChildNodeData[];
  onNavigate?: (p: string) => void;
}) {
  const Icon = category.icon;
  return (
    <div className="rounded-xl border border-surface-700/60 bg-surface-800/40 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: category.accent }} />
        <h4 className="text-sm font-semibold text-surface-100">{category.label}</h4>
        <span className="text-xs text-surface-400">
          {total} {total === 1 ? "item" : "items"}
          {children.length < total && <> · showing {children.length}</>}
        </span>
      </div>

      {children.length === 0 ? (
        <p className="text-xs text-surface-400">
          Nothing linked to this asset yet.
        </p>
      ) : (
        <ul className="divide-y divide-surface-700/40">
          {children.map((child) => (
            <li key={child.id} className="py-2 first:pt-0 last:pb-0">
              <ChildRow child={child} accent={category.accent} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChildRow({
  child, accent, onNavigate,
}: {
  child: ChildNodeData;
  accent: string;
  onNavigate?: (p: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: accent }}
          />
          <span className="truncate text-sm text-surface-100">{child.label}</span>
        </div>
        <div className="ml-3.5 mt-0.5">
          <ChildSummary child={child} />
        </div>
      </div>
      {child.navigateTo && onNavigate && (
        <button
          onClick={() => onNavigate(child.navigateTo!)}
          className="flex shrink-0 items-center gap-1 rounded-md border border-surface-700/60 bg-surface-800/60 px-2 py-1 text-[11px] text-surface-300 hover:bg-surface-700/60 hover:text-surface-100"
          title="Open in dedicated view"
        >
          <ExternalLink className="h-3 w-3" />
          Open
        </button>
      )}
    </div>
  );
}

// ── Per-category compact summary line ────────────────────────────────

function ChildSummary({ child }: { child: ChildNodeData }) {
  switch (child.category) {
    case "floc": {
      const f = child.raw as FunctionalLocationSummary;
      return (
        <span className="text-[11px] text-surface-400">
          L{f.level} · <span className="font-mono">{f.code}</span>
          {f.description ? ` · ${truncate(f.description, 50)}` : ""}
        </span>
      );
    }
    case "tags": {
      const t = child.raw as TagItem;
      const g = t.geometry as { x?: number; y?: number; z?: number };
      const source = t.digital_twin_id ? "Digital twin" : t.point_cloud_id ? "Point cloud" : "—";
      const isPoint = typeof g.x === "number" && typeof g.y === "number" && typeof g.z === "number";
      return (
        <span className="text-[11px] text-surface-400">
          {t.tag_type} · {source}
          {isPoint && (
            <>
              {" · "}
              <span className="font-mono">
                ({g.x!.toFixed(1)}, {g.y!.toFixed(1)}, {g.z!.toFixed(1)})
              </span>
            </>
          )}
        </span>
      );
    }
    case "instrumentation": {
      const raw = child.raw as InstrumentationDevice | InstrumentationSensor;
      if ("sensor_count" in raw) {
        const d = raw as InstrumentationDevice;
        return (
          <span className="flex flex-wrap items-center gap-1 text-[11px] text-surface-400">
            <Cpu className="h-3 w-3" /> Device · {d.source_type.replace(/_/g, " ")}
            {d.manufacturer && ` · ${d.manufacturer}`}
            {` · ${d.sensor_count} ${d.sensor_count === 1 ? "sensor" : "sensors"}`}
          </span>
        );
      }
      const s = raw as InstrumentationSensor;
      const data = (s.data || {}) as { value?: unknown; ts?: string };
      const valueText =
        typeof data.value === "number"
          ? data.value.toFixed(2)
          : data.value !== undefined
            ? String(data.value)
            : null;
      return (
        <span className="flex flex-wrap items-center gap-1 text-[11px] text-surface-400">
          <Radio className="h-3 w-3" /> Sensor
          {s.sensor_type && ` · ${s.sensor_type}`}
          {valueText !== null && (
            <span className="font-mono text-brand-300">
              {" · "}{valueText}{s.unit ? ` ${s.unit}` : ""}
            </span>
          )}
          {data.ts && ` · ${formatDate(data.ts)}`}
        </span>
      );
    }
    case "documents": {
      const d = child.raw as DMSDocument;
      const cur = d.versions.find((v) => v.is_current) ?? d.versions[d.versions.length - 1];
      return (
        <span className="flex flex-wrap items-center gap-1 text-[11px] text-surface-400">
          <Badge variant="default">{d.document_type.toUpperCase()}</Badge>
          {cur && <span>v{cur.version_number}</span>}
          {cur && <span>· {(cur.file_size_bytes / 1024).toFixed(1)} KB</span>}
          {d.folder_path && (
            <span className="inline-flex items-center gap-1">
              · <FileText className="h-3 w-3" /> {truncate(d.folder_path, 30)}
            </span>
          )}
          <span>· {formatDate(d.updated_at)}</span>
        </span>
      );
    }
    case "maintenance": {
      const m = child.raw as MaintenanceTask;
      return (
        <span className="flex flex-wrap items-center gap-1 text-[11px] text-surface-400">
          <StatusBadge status={m.status} />
          {m.task_type && <span className="capitalize">· {m.task_type}</span>}
          {m.priority && (
            <Badge variant={m.priority === "high" ? "danger" : m.priority === "medium" ? "warning" : "default"}>
              {m.priority}
            </Badge>
          )}
          {m.due_date && <span>· due {formatDate(m.due_date)}</span>}
        </span>
      );
    }
    case "risk": {
      const r = child.raw as RiskAssessment;
      return (
        <span className="flex flex-wrap items-center gap-2 text-[11px] text-surface-400">
          <span>L{r.likelihood ?? "—"} × C{r.consequence ?? "—"}</span>
          <StatusBadge status={r.status} />
          <span>· {formatDate(r.assessed_at)}</span>
        </span>
      );
    }
    case "history": {
      const h = child.raw as HistoryEntry;
      return (
        <span className="flex flex-wrap items-center gap-2 text-[11px] text-surface-400">
          <Badge variant={h.action === "created" ? "success" : h.action === "deleted" ? "danger" : "default"}>
            {h.action}
          </Badge>
          <span>{formatDateTime(h.changed_at)}</span>
          {h.description && <span>· {truncate(h.description, 40)}</span>}
        </span>
      );
    }
  }
}
